/**
@format
@flow
*/
import * as React from 'react';
import styled from 'styled-components';
import { Input as KrispyInput } from '@krispy/inputs/web';
import { Constants } from '@ziynx/components/layout';
import Colors from '@ziynx/theme/colors';

type Props = $ReadOnly<{|
  /**
   * Type of input
   */
  variant?: 'number' | 'text' | 'phone' | 'password' | 'checkbox' | 'textarea' | 'file' | 'slider',

  /**
   * If error
   */
  error?: boolean,

  /**
   * If input is disabled
   */
  disabled?: boolean,

  /**
   * If true, input is required
   */
  required?: boolean,

  /**
   * If true, input is read-only, can't edit
   */
  readOnly?: boolean,

  /**
   * If true, input will default to lowercase text
   */
  lowercase?: boolean,

  /**
   * Input label
   */
  label: string,

  /**
   * Aria label for inputs
   * Default is empty string
   * and will be passed to variants with inputName
   * for default
   */
  ariaLabel?: string,

  /**
   * Input label color
   */
  labelColor?: string,

  /**
   * Error label if error is true
   */
  errorLabel?: string,

  /**
   * Error label color if error is true
   */
  errorLabelColor?: string,

  /**
   * Required label color if required is true
   */
  requiredColor?: string,

  /**
   * Name for the input to be handled in forms
   */
  inputName: string,

  /**
   * Width of input, string or number
   * Default 100%
   */
  width?: string,

  /**
   * Height of the input
   */
  height?: string,

  /**
   * Helper text, if any
   */
  helperText?: string,

  /**
   * Character limit for char count functionality
   * Default error message when exceeded is "Character limit exceeded."
   * Helper text will be overridden if there's an error
   */
  charLimit?: number,

  /**
   * Custom onChange fired when user types in input
   */
  onChange?: (event: SyntheticInputEvent<HTMLInputElement>) => void,

  /**
   * Custom onBlur
   */
  onBlur?: (event: SyntheticInputEvent<HTMLInputElement>) => void,

  /**
   * Custom onFocus
   */
  onFocus?: (event: SyntheticInputEvent<HTMLInputElement>) => void,

|}>;

const StyledInput = styled(KrispyInput)`
  height: ${({ height }) => height};
`;

/**
 * Input component that renders different types of input
 */
export default function Input(props: Props): React$Node {
  const { height, ...rest } = props;
  return (
    <StyledInput layoutConfig={Constants} height={height} {...rest} />
  );
}

Input.defaultProps = {
  variant: 'text',
  labelColor: Colors.Gray,
  requiredColor: Colors.SecondaryRed,
  error: false,
  disabled: false,
  required: false,
  readOnly: false,
  width: '100%',
  ariaLabel: '',
  errorLabel: '',
  charLimit: undefined,
  errorLabelColor: Colors.SecondaryRed,
  onChange: () => {},
  onBlur: () => {},
  onFocus: () => {},
  lowercase: false,
};
